import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { usePartnerSearch, type PartnerSearchResult } from '@/api/extensions/usePartnerSearch';
import { useDebounce } from '@/hooks/useDebounce';

interface PartnerSearchComboboxProps {
  /** Selected user from search (null when nothing chosen) */
  value: PartnerSearchResult | null;
  onChange: (user: PartnerSearchResult | null) => void;
  partnerPosition: string;
  error?: string;
  /** Filter to only show users of this gender (for mixed-gender events) */
  filterGender?: string;
  /** Minimum partner age in years (for century jackpot events) */
  minPartnerAge?: number;
  /**
   * Event the inviter is registering for. When supplied, the dropdown
   * decorates each result with the event's per-contestant entry cap and
   * blocks selection on contestants who are already at the cap — so the
   * inviter sees them as ineligible up-front instead of submitting an
   * invite that the backend rejects with PARTNER_AT_MAX_ENTRIES.
   */
  eventId?: string | null;
  /**
   * Profile id of the *registrant* — the user the registration is being
   * created for. Excluded from search results (you can't be your own
   * partner). In the on-behalf buddy-group flow this is the teammate
   * being registered, NOT the logged-in actor — which means the actor is
   * a valid candidate partner for the teammate and will appear in
   * results. In self-registration leave this undefined; the backend
   * defaults to actor-self-exclusion via auth_user_id.
   */
  excludeUserProfileId?: string | null;
}

export function PartnerSearchCombobox({
  value,
  onChange,
  partnerPosition,
  error,
  filterGender,
  minPartnerAge,
  eventId,
  excludeUserProfileId,
}: PartnerSearchComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trimmedQuery = query.trim().toLowerCase();
  const isFullEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedQuery);
  // Resolve a full email faster than a name fragment so the auto-select
  // below has the matching account ready by the time the user clicks Next.
  const debouncedQuery = useDebounce(query, isFullEmail ? 150 : 500);
  const { data: results = [], isLoading } = usePartnerSearch(debouncedQuery, {
    gender: filterGender,
    minPartnerAge,
    eventId,
    excludeUserProfileId,
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-select when the typed query is a full email address that exactly
  // matches a search result. The partner search already matches on email,
  // so a user who types a known partner's email gets that account resolved
  // for them — they can hit Next without having to click the dropdown row.
  // Exact (case-insensitive) match only, so partial/in-progress typing
  // never auto-picks the wrong person, and contestants already at the
  // event entry cap are skipped (same guard as a manual click).
  useEffect(() => {
    if (value || !isFullEmail) return;
    const match = results.find(
      (u) => (u.primary_email ?? '').trim().toLowerCase() === trimmedQuery,
    );
    if (match && match.at_max_entries !== true) {
      onChange(match);
      setQuery('');
      setOpen(false);
    }
  }, [results, trimmedQuery, isFullEmail, value, onChange]);

  // Fall back to legal first/last name when display names are blank — Google
  // OAuth signups frequently leave display_first_name / display_last_name empty,
  // and "Unknown" in the dropdown was making real users look like ghost rows.
  const displayName = (u: PartnerSearchResult) => {
    const first = u.display_first_name || u.first_name || '';
    const last = u.display_last_name || u.last_name || '';
    const combined = [first, last].filter(Boolean).join(' ').trim();
    return combined || u.primary_email || 'Unknown';
  };

  // Selected state — show the chosen partner
  if (value) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-lg border border-primary bg-primary/5 p-3">
          <div className="flex-1">
            <p className="font-medium text-sm">{displayName(value)}</p>
          </div>
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
            {partnerPosition}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // Search mode (default)
  return (
    <div ref={wrapperRef} className="relative space-y-1">
      <Input
        placeholder="Search by name or email..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        className={error ? 'border-destructive' : ''}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <p className="text-xs text-muted-foreground">
        Enter a partial name to filter, or a full email address if you know it.
        They will rope as <strong>{partnerPosition}</strong>.
      </p>

      {open && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
          {isLoading && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching...</p>
          )}
          {!isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No users found</p>
          )}
          {results.map((user: PartnerSearchResult) => {
            const atMax = user.at_max_entries === true;
            // Note: at_max_entries is only set when the search request
            // included event_id; in other contexts (admin lookup) it is
            // undefined and the picker stays selectable. The cap label
            // also only renders when we have a known max.
            const ineligibleReason = atMax
              ? user.max_entries === 1
                ? 'Already entered for this event'
                : `At entry limit (${user.active_entry_count ?? user.max_entries} of ${user.max_entries})`
              : null;
            return (
              <button
                key={user.id}
                type="button"
                disabled={atMax}
                aria-disabled={atMax}
                title={ineligibleReason ?? undefined}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  atMax
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:bg-accent'
                }`}
                onClick={() => {
                  if (atMax) return;
                  onChange(user);
                  setQuery('');
                  setOpen(false);
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{displayName(user)}</p>
                  {ineligibleReason && (
                    <p className="text-xs text-destructive mt-0.5">
                      {ineligibleReason}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {partnerPosition}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
